import { useState } from 'react';
import { scoreRewards, leaderboard } from '@/mocks/community';
import FarmGame from './FarmGame';
import FriendFarm from './FriendFarm';

interface ScoreRewardPanelProps {
  currentUser: { name: string; role: string } | null;
  onOpenAuth: () => void;
}

const rewardItems = [
  { score: 100, name: '学习文具包', icon: 'ri-pen-nib-line', color: 'bg-amber-50 text-amber-600' },
  { score: 300, name: '精选课程券', icon: 'ri-video-line', color: 'bg-blue-50 text-blue-600' },
  { score: 500, name: '爱心捐书名额', icon: 'ri-book-open-line', color: 'bg-green-50 text-green-600' },
  { score: 1000, name: '荣誉证书', icon: 'ri-award-line', color: 'bg-rose-50 text-rose-600' },
];

export default function ScoreRewardPanel({ currentUser, onOpenAuth }: ScoreRewardPanelProps) {
  const [activeTab, setActiveTab] = useState<'farm' | 'friends' | 'earn' | 'rewards' | 'rank'>('farm');
  const [checkedIn, setCheckedIn] = useState(false);
  const [userScore, setUserScore] = useState(280);
  const [streak] = useState(5);

  const handleCheckIn = () => {
    if (!currentUser) { onOpenAuth(); return; }
    if (checkedIn) return;
    setCheckedIn(true);
    setUserScore(prev => prev + 10);
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
      {/* Score Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-5 text-white">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-white/80 text-xs mb-1">我的积分</p>
            <p className="text-3xl font-black">{currentUser ? userScore : '---'}</p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 justify-end mb-1">
              <i className="ri-fire-line text-orange-200"></i>
              <span className="text-white/90 text-sm font-bold">连续 {streak} 天</span>
            </div>
            <p className="text-white/70 text-xs">每周登录赚积分</p>
          </div>
        </div>
        <button
          onClick={handleCheckIn}
          disabled={checkedIn}
          className={`w-full py-2.5 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
            checkedIn ? 'bg-white/20 text-white/60' : 'bg-white text-amber-600 hover:bg-amber-50'
          }`}
        >
          {checkedIn ? (
            <><i className="ri-check-line mr-1"></i>今日已签到 +10分</>
          ) : (
            <><i className="ri-calendar-check-line mr-1"></i>每日签到领积分</>
          )}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 overflow-x-auto">
        {[
          { key: 'farm', label: '🌾 农场' },
          { key: 'friends', label: '👥 好友' },
          { key: 'earn', label: '赚积分' },
          { key: 'rewards', label: '兑换' },
          { key: 'rank', label: '排行' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as 'farm' | 'friends' | 'earn' | 'rewards' | 'rank')}
            className={`flex-1 py-3 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.key ? 'text-amber-600 border-b-2 border-amber-500' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Farm Tab - Full Game */}
      {activeTab === 'farm' && (
        <div className="p-0">
          <FarmGame
            currentUser={currentUser}
            onOpenAuth={onOpenAuth}
            userScore={userScore}
          />
        </div>
      )}

      {activeTab === 'friends' && (
        <div className="p-0">
          <FriendFarm currentUser={currentUser} onOpenAuth={onOpenAuth} />
        </div>
      )}

      {activeTab === 'earn' && (
        <div className="p-4">
          <div className="bg-green-50 rounded-xl p-3 mb-3 flex items-start gap-2">
            <i className="ri-plant-line text-green-600 text-sm mt-0.5"></i>
            <p className="text-green-700 text-xs leading-relaxed">
              完成学习任务赚积分，积分越多农场解锁的蔬菜越多！
            </p>
          </div>
          <div className="flex flex-col gap-2">
            {scoreRewards.map((item) => (
              <div key={item.action} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center bg-gray-50 rounded-lg">
                    <i className={`${item.icon} text-sm ${item.color}`}></i>
                  </div>
                  <span className="text-sm text-gray-700">{item.action}</span>
                </div>
                <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">+{item.score}分</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'rewards' && (
        <div className="p-4">
          <div className="grid grid-cols-2 gap-2">
            {rewardItems.map((item) => (
              <div key={item.name} className="border border-gray-100 rounded-xl p-3 text-center">
                <div className={`w-10 h-10 flex items-center justify-center rounded-full mx-auto mb-2 ${item.color.split(' ')[0]}`}>
                  <i className={`${item.icon} text-lg ${item.color.split(' ')[1]}`}></i>
                </div>
                <p className="text-xs font-semibold text-gray-800 mb-1">{item.name}</p>
                <p className="text-xs text-amber-600 font-bold mb-2">{item.score}积分</p>
                <button
                  onClick={() => !currentUser && onOpenAuth()}
                  className={`w-full py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                    currentUser && userScore >= item.score
                      ? 'bg-amber-500 text-white hover:bg-amber-600'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {currentUser && userScore >= item.score ? '立即兑换' : '积分不足'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'rank' && (
        <div className="p-4">
          <div className="flex flex-col gap-2">
            {leaderboard.map((user) => (
              <div key={user.rank} className="flex items-center gap-3">
                <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-black flex-shrink-0 ${
                  user.rank === 1 ? 'bg-amber-400 text-white' :
                  user.rank === 2 ? 'bg-gray-300 text-white' :
                  user.rank === 3 ? 'bg-amber-700 text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                  {user.rank}
                </div>
                <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full object-cover object-top flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate">{user.name}</p>
                  <p className="text-[10px] text-gray-400">{user.badge}</p>
                </div>
                <span className="text-xs font-bold text-amber-600">{user.score.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
